import React from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./ConsultationForm.css";

const TEST_RECAPTCHA_SITE_KEY = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";
const DEFAULT_SUBJECTS = [
  "Full-Cycle Game Development",
  "Unity Game Development",
  "Unreal Engine Development",
  "2D & 3D Game Development",
  "Multiplayer Game Development",
  "Mobile Game Development",
  "AR/VR/MR Game Development",
  "Game Art, Animation & VFX",
  "Game QA & LiveOps Support",
];

const getTodayDate = () => new Date().toISOString().split("T")[0];

class ConsultationForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      name: "",
      email: "",
      number: "",
      subject: props.defaultSubject || "",
      message: "",
      toDate: props.defaultTodayDate ? getTodayDate() : props.defaultDate || "",
      recaptchaToken: "",
      formErrors: {},
    };

    this.nameRef = React.createRef();
    this.emailRef = React.createRef();
    this.subjectRef = React.createRef();
    this.numberRef = React.createRef();
    this.messageRef = React.createRef();
    this.toDateRef = React.createRef();
    this.recaptchaRef = React.createRef();
    this.recaptchaWidgetId = null;
    this.recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || TEST_RECAPTCHA_SITE_KEY;

    this.handleChange = this.handleChange.bind(this);
    this.maxlengthnumber = this.maxlengthnumber.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.loadRecaptcha = this.loadRecaptcha.bind(this);
    this.renderRecaptcha = this.renderRecaptcha.bind(this);
    this.handleRecaptchaChange = this.handleRecaptchaChange.bind(this);
    this.handleRecaptchaExpired = this.handleRecaptchaExpired.bind(this);
  }

  componentDidMount() {
    this.loadRecaptcha();
  }

  loadRecaptcha() {
    if (!this.recaptchaSiteKey) {
      return;
    }

    if (window.grecaptcha?.render) {
      window.grecaptcha.ready ? window.grecaptcha.ready(this.renderRecaptcha) : this.renderRecaptcha();
      return;
    }

    const scriptSrc = "https://www.google.com/recaptcha/api.js?render=explicit";
    const existingScript = document.getElementById("google-recaptcha-script");

    if (existingScript) {
      if (existingScript.src.includes("/recaptcha/enterprise.js")) {
        existingScript.remove();
      } else {
        existingScript.addEventListener("load", () => {
          window.grecaptcha?.ready ? window.grecaptcha.ready(this.renderRecaptcha) : this.renderRecaptcha();
        }, { once: true });
        return;
      }
    }

    const script = document.createElement("script");
    script.id = "google-recaptcha-script";
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha?.ready ? window.grecaptcha.ready(this.renderRecaptcha) : this.renderRecaptcha();
    };
    document.body.appendChild(script);
  }

  renderRecaptcha() {
    if (!this.recaptchaRef.current || !window.grecaptcha?.render || this.recaptchaWidgetId !== null) {
      return;
    }

    this.recaptchaWidgetId = window.grecaptcha.render(this.recaptchaRef.current, {
      sitekey: this.recaptchaSiteKey,
      callback: this.handleRecaptchaChange,
      "expired-callback": this.handleRecaptchaExpired,
      "error-callback": this.handleRecaptchaExpired,
    });
  }

  handleRecaptchaChange(token) {
    this.setState((prevState) => ({
      recaptchaToken: token,
      formErrors: {
        ...prevState.formErrors,
        recaptchaError: "",
      },
    }));
  }

  handleRecaptchaExpired() {
    this.setState({ recaptchaToken: "" });
  }

  handleChange(e) {
    this.setState({ [e.target.name]: e.target.value });
  }

  maxlengthnumber(e) {
    if (e.target.value.length <= 10) {
      this.handleChange(e);
    }
  }

  resetRecaptcha() {
    if (window.grecaptcha && this.recaptchaWidgetId !== null) {
      window.grecaptcha.reset(this.recaptchaWidgetId);
    }
  }

  resetForm() {
    this.setState({
      name: "",
      email: "",
      number: "",
      subject: this.props.defaultSubject || "",
      message: "",
      toDate: this.props.defaultTodayDate ? getTodayDate() : this.props.defaultDate || "",
      recaptchaToken: "",
      formErrors: {},
    });
  }

  onSubmit(e) {
    e.preventDefault();

    const formErrors = {};
    const validations = [
      ["name", "nameError", "Name is required.", this.nameRef],
      ["email", "emailError", "Email id is required.", this.emailRef],
      ["subject", "subjectError", "Subject is required.", this.subjectRef],
      ["number", "numberError", "Number is required.", this.numberRef],
      ["message", "messageError", "Message is required.", this.messageRef],
      ["toDate", "toDateError", "Date is required.", this.toDateRef],
    ];

    for (const [field, errorKey, message, ref] of validations) {
      if (!this.state[field]) {
        formErrors[errorKey] = message;
        this.setState({ formErrors });
        ref.current?.focus();
        return false;
      }
    }

    if (!this.state.recaptchaToken) {
      formErrors.recaptchaError = "Please verify that you are not a robot.";
      this.setState({ formErrors });
      return false;
    }

    const subjectPrefix = this.props.subjectPrefix ? `${this.props.subjectPrefix}-` : "";
    const formObject = {
      name: this.state.name,
      email: this.state.email,
      number: this.state.number,
      subject: `${subjectPrefix}${this.state.subject}`,
      toDate: this.state.toDate,
      message: this.state.message,
      recaptchaToken: this.state.recaptchaToken,
    };

    axios.post("/contactus", formObject)
      .then((res) => {
        this.resetRecaptcha();
        if (res.data !== "undefined") {
          Swal.fire({
            title: "Thank you!",
            text: "Your request has been assigned successfully.",
            icon: "success",
          }).then(() => {
            this.resetForm();
            if (this.props.reloadOnSuccess) {
              window.location.reload();
            }
          });
        } else {
          Swal.fire({
            title: "Oops...!",
            text: "Oh! Something went wrong, please try again!",
            icon: "error",
          });
        }
      })
      .catch((error) => {
        this.resetRecaptcha();
        this.setState({ recaptchaToken: "" });
        Swal.fire({
          title: "Verification failed",
          text: error.response?.data?.message || "Please complete reCAPTCHA and try again.",
          icon: "error",
        });
      });

    return true;
  }

  render() {
    const {
      nameError,
      emailError,
      subjectError,
      numberError,
      toDateError,
      messageError,
      recaptchaError,
    } = this.state.formErrors;
    const todayDate = getTodayDate();
    const subjects = this.props.subjects || DEFAULT_SUBJECTS;
    const formClassName = `appoitment-form ajax-contact consultation-form ${this.props.className || ""}`.trim();
    const fieldClassName = this.props.fieldClassName || "";
    const buttonClassName = this.props.buttonClassName || "as-btn style3";
    const buttonText = this.props.buttonText || "Send Consultation Request";
    const useSubjectInput = Boolean(this.props.subjectInput);

    return (
      <form onSubmit={this.onSubmit} method="post" className={formClassName}>
        <div className="row">
          <div className={`form-group col-md-6 ${fieldClassName}`}>
            <input type="text" ref={this.nameRef} className={nameError ? "form-control is-invalid" : "form-control"} name="name" id="name" placeholder="Your Name" value={this.state.name} onChange={this.handleChange} autoComplete="off" />
            <i className="fal fa-user"></i>
            {nameError && <div className="invalid-feedback">{nameError}</div>}
          </div>
          <div className={`form-group col-md-6 ${fieldClassName}`}>
            <input type="email" className={emailError ? "form-control is-invalid" : "form-control"} ref={this.emailRef} name="email" id="email" placeholder="Email Address" value={this.state.email} onChange={this.handleChange} autoComplete="off" />
            <i className="fal fa-envelope"></i>
            {emailError && <div className="invalid-feedback">{emailError}</div>}
          </div>
          <div className={`form-group col-md-6 ${fieldClassName}`}>
            {useSubjectInput ? (
              <>
                <input type="text" id="subject" className={subjectError ? "form-control is-invalid" : "form-control"} ref={this.subjectRef} name="subject" placeholder="Subject" value={this.state.subject} onChange={this.handleChange} autoComplete="off" />
                <i className="fal fa-note-sticky"></i>
              </>
            ) : (
              <select name="subject" id="subject" ref={this.subjectRef} className={subjectError ? "form-control is-invalid" : "form-control"} value={this.state.subject} onChange={this.handleChange} autoComplete="off">
                <option value="" disabled>Select Subject</option>
                {subjects.map((subject) => (
                  <option value={subject} key={subject}>{subject}</option>
                ))}
              </select>
            )}
            {subjectError && <div className="invalid-feedback">{subjectError}</div>}
          </div>
          <div className={`form-group col-md-6 ${fieldClassName}`}>
            <input type="number" className={numberError ? "form-control is-invalid" : "form-control"} name="number" value={this.state.number} ref={this.numberRef} id="number" placeholder="Phone Number" autoComplete="off" onChange={this.maxlengthnumber} />
            <i className="fal fa-phone"></i>
            {numberError && <div className="invalid-feedback">{numberError}</div>}
          </div>
          <div className={`form-group col-12 ${fieldClassName}`}>
            <input className="form-control datetimepicker" id="datetimepicker" name="toDate" value={this.state.toDate} onChange={this.handleChange} type="date" ref={this.toDateRef} autoComplete="off" min={todayDate} aria-label="Consultation Date" />
            <i className="fal fa-calendar"></i>
            {toDateError && <div className="invalid-feedback">{toDateError}</div>}
          </div>
          <div className={`form-group col-12 ${fieldClassName}`}>
            <textarea name="message" id="message" cols="30" rows="3" ref={this.messageRef} className={messageError ? "form-control is-invalid" : "form-control"} placeholder="Your Message" value={this.state.message} onChange={this.handleChange} autoComplete="off"></textarea>
            <i className="fal fa-comment"></i>
            {messageError && <div className="invalid-feedback">{messageError}</div>}
          </div>
          <div className={`form-group col-12 ${fieldClassName} consultation-recaptcha-field`}>
            <div className="consultation-recaptcha" ref={this.recaptchaRef}></div>
            {recaptchaError && <div className="invalid-feedback d-block">{recaptchaError}</div>}
          </div>
          <div className="form-btn text-xl-start text-center col-12 cont-btn">
            <button type="submit" className={buttonClassName}>{buttonText}
              <i className="fa-regular fa-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
        <p className="form-messages mb-0 mt-3"></p>
      </form>
    );
  }
}

export default ConsultationForm;
